import React from 'react';
import DashMascot from './DashMascot';

/**
 * Full-screen branded loader used during slower transitions:
 *   • SSO redirect to Microsoft (the moment between click and page navigation)
 *   • SSO callback (token exchange + role resolution before /admin renders)
 *   • Any other "next action will take more than a heartbeat" moment
 *
 * Lives on top of the app with a soft teal scrim so the page-behind doesn't
 * flash mismatched UI while we settle.
 *
 * Props
 *   message  — primary line (e.g., "Signing you in…")
 *   sub      — optional secondary line
 *   variant  — "overlay" (default — covers viewport) | "inline" (fills parent)
 */
export default function PageLoader({
  message = 'Signing you in…',
  sub = 'Hang tight while we set up your DASH session.',
  variant = 'overlay',
}) {
  const isOverlay = variant === 'overlay';
  return (
    <div
      style={{
        position: isOverlay ? 'fixed' : 'absolute',
        inset: 0,
        zIndex: isOverlay ? 4000 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(circle at 30% 20%, rgba(79,209,197,0.25) 0%, transparent 55%),' +
          'radial-gradient(circle at 70% 80%, rgba(29,156,156,0.30) 0%, transparent 55%),' +
          'linear-gradient(135deg, #0a1f33 0%, #0d4f52 50%, #061829 100%)',
        animation: 'pl-fade-in 0.18s ease',
      }}
    >
      <style>{`
        @keyframes pl-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pl-spin    { to { transform: rotate(360deg); } }
        @keyframes pl-pulse   {
          0%, 100% { transform: scale(1);    opacity: 0.85; }
          50%      { transform: scale(1.06); opacity: 1;    }
        }
        @keyframes pl-dot {
          0%, 80%, 100% { opacity: 0.25; }
          40%           { opacity: 1; }
        }
      `}</style>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 26,
        padding: '32px 40px',
      }}>
        {/* Branded mark + spinning ring */}
        <div style={{ position: 'relative', width: 96, height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute', inset: 0,
            border: '3px solid rgba(255,255,255,0.10)',
            borderTopColor: '#4fd1c5',
            borderRightColor: '#1d9c9c',
            borderRadius: '50%',
            animation: 'pl-spin 1.1s linear infinite',
          }} />
          <div style={{
            width: 70, height: 70,
            background: '#ffffff',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 12px 32px rgba(13, 79, 82, 0.45)',
            animation: 'pl-pulse 1.6s ease-in-out infinite',
          }}>
            <DashMascot size={48} title="DASH" />
          </div>
        </div>

        {/* Message */}
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{
            color: '#ffffff',
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: '-0.01em',
            fontFamily: 'var(--font-heading)',
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 4,
          }}>
            {message}
            <span style={{ display: 'inline-flex', gap: 3, marginLeft: 4 }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: '#4fd1c5',
                  animation: `pl-dot 1.2s ease-in-out ${i * 0.18}s infinite`,
                  display: 'inline-block',
                }} />
              ))}
            </span>
          </div>
          {sub && (
            <div style={{
              marginTop: 8,
              color: 'rgba(255,255,255,0.65)',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.5,
            }}>{sub}</div>
          )}
        </div>
      </div>
    </div>
  );
}
